/**
 * Storage Manager - Delegates to Storage Provider
 * This is a compatibility layer that maintains the existing API
 * while delegating to the storage provider system
 */

import {
  getStorageProvider,
  initializeStorage as initStorage,
  isStorageInitialized as isStorageInit
} from './storage/index';
import type {
  Transaction,
  TransactionUpdate,
  SplitInfo,
  ProcessedFile,
  Rule,
  RuleCreateData,
  SaveTransactionsResult,
  BulkUpdateResult,
  SplitTransactionResult,
  UnsplitTransactionResult,
  ApplyRuleResult,
  RuleExistsResult,
  RuleMatchResult
} from './types';
import type {
  BudgetData,
  BudgetSettings,
  BudgetStatus,
  BudgetCreateData,
  BudgetUpdateData,
  Budget
} from './types/budget';

// Re-export types for convenience
export type {
  Transaction,
  TransactionUpdate,
  SplitInfo,
  ProcessedFile,
  Rule,
  RuleCreateData,
  SaveTransactionsResult,
  BulkUpdateResult,
  SplitTransactionResult,
  UnsplitTransactionResult,
  ApplyRuleResult,
  RuleExistsResult,
  RuleMatchResult
};

// Re-export storage initialization functions with original names
export { initStorage as initializeStorage, getStorageProvider, getStorageProvider as getStorage, isStorageInit as isStorageInitialized };

// ============================================================================
// Transaction Operations
// ============================================================================

export async function getTransactions(): Promise<Transaction[]> {
  const provider = getStorageProvider();
  return provider.getTransactions();
}

export async function saveTransactionsForFile(
  csvFilename: string,
  transactions: Transaction[]
): Promise<SaveTransactionsResult> {
  const provider = getStorageProvider();
  return provider.saveTransactionsForFile(csvFilename, transactions);
}

export async function upsertTransaction(transaction: Transaction): Promise<boolean> {
  const provider = getStorageProvider();
  return provider.upsertTransaction(transaction);
}

export async function bulkUpdateByDescription(
  description: string,
  updates: TransactionUpdate
): Promise<BulkUpdateResult> {
  const provider = getStorageProvider();
  return provider.bulkUpdateTransactions(description, updates);
}

export async function undoBulkUpdate(previousStates: Transaction[]): Promise<BulkUpdateResult> {
  const provider = getStorageProvider();
  return provider.undoBulkUpdate(previousStates);
}

export async function splitTransaction(
  transactionId: string,
  splitInfo: SplitInfo[]
): Promise<SplitTransactionResult> {
  const provider = getStorageProvider();
  return provider.splitTransaction(transactionId, splitInfo);
}

export async function unsplitTransaction(transactionId: string): Promise<UnsplitTransactionResult> {
  const provider = getStorageProvider();
  return provider.unsplitTransaction(transactionId);
}

export async function deleteAllTransactions(): Promise<boolean> {
  const provider = getStorageProvider();
  return provider.deleteAllTransactions();
}

export async function deleteTransactionsFromFile(filename: string): Promise<{ success: boolean; deletedCount: number; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteTransactionsFromFile(filename);
}

// ============================================================================
// File Tracking
// ============================================================================

export async function getProcessedFiles(): Promise<ProcessedFile[]> {
  const provider = getStorageProvider();
  return provider.getProcessedFiles();
}

export async function markFileAsProcessed(
  filename: string,
  transactionCount: number,
  fileHash?: string,
  metadata?: { bank?: string; person?: string; accountName?: string; accountType?: string }
): Promise<boolean> {
  const provider = getStorageProvider();
  return provider.markFileAsProcessed(filename, transactionCount, fileHash, metadata);
}

export async function isFileProcessed(filename: string): Promise<boolean> {
  const provider = getStorageProvider();
  return provider.isFileProcessed(filename);
}

export async function isFileHashProcessed(fileHash: string): Promise<{ processed: boolean; filename?: string }> {
  const provider = getStorageProvider();
  return provider.isFileHashProcessed(fileHash);
}

export async function deleteProcessedFile(filename: string): Promise<{ success: boolean; deletedTransactions: number; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteProcessedFile(filename);
}

// ============================================================================
// Category Management
// ============================================================================

export async function getCategories(): Promise<string[]> {
  const provider = getStorageProvider();
  return provider.getCategories();
}

export async function addCategory(category: string): Promise<{ success: boolean; categories?: string[]; error?: string }> {
  const provider = getStorageProvider();
  return provider.addCategory(category);
}

export async function updateCategory(
  oldCategory: string,
  newCategory: string
): Promise<{ success: boolean; affectedTransactions?: number; error?: string }> {
  const provider = getStorageProvider();
  return provider.updateCategory(oldCategory, newCategory);
}

export async function deleteCategory(
  category: string
): Promise<{ success: boolean; affectedTransactions?: number; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteCategory(category);
}

// ============================================================================
// Category Alias Resolution
// ============================================================================

export async function resolveCategoryAlias(categoryName: string): Promise<string> {
  const provider = getStorageProvider();
  return provider.resolveCategoryAlias(categoryName);
}

export async function getCategoryAliases(): Promise<Record<string, string>> {
  const provider = getStorageProvider();
  return provider.getCategoryAliases();
}

// ============================================================================
// Institution Management
// ============================================================================

export async function getInstitutions(): Promise<string[]> {
  const provider = getStorageProvider();
  return provider.getInstitutions();
}

// ============================================================================
// Transaction Review
// ============================================================================

export interface ReviewableTransactionsResult {
  transactions: Transaction[];
  mediumConfidence: Transaction[];
  uncategorized: Transaction[];
  count: number;
}

export async function getReviewableTransactions(): Promise<ReviewableTransactionsResult> {
  const provider = getStorageProvider();
  return provider.getReviewableTransactions();
}

// ============================================================================
// People Management
// ============================================================================

export async function getPeople(): Promise<string[]> {
  const provider = getStorageProvider();
  return provider.getPeople();
}

// ============================================================================
// Rule Management
// ============================================================================

export async function getRules(): Promise<Rule[]> {
  const provider = getStorageProvider();
  return provider.getRules();
}

export async function createRule(
  ruleData: RuleCreateData
): Promise<{ success: boolean; rule?: Rule; error?: string }> {
  const provider = getStorageProvider();
  // Extract fields from ruleData to match provider signature
  const { pattern, category, person, ...options } = ruleData;
  return provider.createRule(pattern, category, person || null, options);
}

export async function createRulesFromReview(decisions: Array<{
  description: string;
  category: string;
  person?: string | null;
}>): Promise<{ created: number; skipped: number; skippedRules: any[]; createdRules: Rule[] }> {
  const provider = getStorageProvider();
  return provider.createRulesFromReview(decisions);
}

export async function updateRule(
  ruleId: string,
  updates: Partial<Rule>
): Promise<{ success: boolean; rule?: Rule; error?: string }> {
  const provider = getStorageProvider();
  return provider.updateRule(ruleId, updates);
}

export async function deleteRule(ruleId: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteRule(ruleId);
}

export async function deleteAllRules(): Promise<boolean> {
  const provider = getStorageProvider();
  return provider.deleteAllRules();
}

export async function applyRuleToExisting(ruleId: string, forceApply: boolean = false): Promise<ApplyRuleResult> {
  const provider = getStorageProvider();
  return provider.applyRuleToExisting(ruleId, forceApply);
}

export async function checkRuleExists(pattern: string): Promise<RuleExistsResult> {
  const provider = getStorageProvider();
  return provider.checkRuleExists(pattern);
}

export async function applyRulesToTransaction(description: string): Promise<RuleMatchResult | null> {
  const provider = getStorageProvider();
  return provider.applyRulesToTransaction(description);
}

// ==================== BUDGETS ====================

export async function getBudgets(): Promise<BudgetData> {
  const provider = getStorageProvider();
  return provider.getBudgets();
}

export async function createBudget(data: BudgetCreateData): Promise<{ success: boolean; budget?: Budget; error?: string }> {
  const provider = getStorageProvider();
  return provider.createBudget(data);
}

export async function updateBudget(
  name: string,
  type: string,
  updates: BudgetUpdateData
): Promise<{ success: boolean; budget?: Budget; error?: string }> {
  const provider = getStorageProvider();
  return provider.updateBudget(name, type, updates);
}

export async function deleteBudget(name: string, type: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteBudget(name, type);
}

export async function deleteAllBudgets(): Promise<boolean> {
  const provider = getStorageProvider();
  return provider.deleteAllBudgets();
}

export async function getBudgetStatus(month: string): Promise<{
  monthly: BudgetStatus[];
  quarterly: BudgetStatus[];
  yearly: BudgetStatus[];
  unbudgeted: BudgetStatus[];
}> {
  const provider = getStorageProvider();
  return provider.getBudgetStatus(month);
}

export async function updateBudgetRollover(
  name: string,
  type: string,
  rolloverAmount: number
): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.updateBudgetRollover(name, type, rolloverAmount);
}

export async function resetBudgetRollover(name: string, type: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.resetBudgetRollover(name, type);
}

export async function getBudgetSettings(): Promise<BudgetSettings> {
  const provider = getStorageProvider();
  return provider.getBudgetSettings();
}

export async function updateBudgetSettings(settings: Partial<BudgetSettings>): Promise<{ success: boolean; settings?: BudgetSettings; error?: string }> {
  const provider = getStorageProvider();
  return provider.updateBudgetSettings(settings);
}

// ==================== RECURRING PATTERN DISMISSALS ====================

export async function dismissRecurringPattern(pattern: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.dismissRecurringPattern(pattern);
}

export async function getDismissedPatterns(): Promise<string[]> {
  const provider = getStorageProvider();
  return provider.getDismissedPatterns();
}

export async function undismissRecurringPattern(pattern: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.undismissRecurringPattern(pattern);
}

export async function snoozeRecurringPattern(pattern: string, days: number): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.snoozeRecurringPattern(pattern, days);
}

export async function getSnoozedPatterns(): Promise<{ pattern: string; snoozedUntil: string }[]> {
  const provider = getStorageProvider();
  return provider.getSnoozedPatterns();
}

export async function updateRecurringInterval(pattern: string, customInterval: number): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.updateRecurringInterval(pattern, customInterval);
}

export async function getRecurringMetadata(pattern: string): Promise<{ customInterval?: number } | null> {
  const provider = getStorageProvider();
  return provider.getRecurringMetadata(pattern);
}

// ==================== USER ACTIONS (PREFERENCES, DISMISSALS, UI STATE) ====================

// Preferences
export async function setUserPreference(key: string, value: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.setUserPreference(key, value);
}

export function getUserPreference(key: string): string | null {
  const provider = getStorageProvider();
  return provider.getUserPreference(key);
}

export function getAllUserPreferences(): Record<string, string> {
  const provider = getStorageProvider();
  return provider.getAllUserPreferences();
}

export async function deleteUserPreference(key: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteUserPreference(key);
}

// Dismissals
export async function dismissItem(key: string, days?: number): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.dismissItem(key, days);
}

export function isDismissed(key: string): boolean {
  const provider = getStorageProvider();
  return provider.isDismissed(key);
}

export function getDismissedItems(): string[] {
  const provider = getStorageProvider();
  return provider.getDismissedItems();
}

export function getDismissedItemsWithExpiry(): Record<string, string | null> {
  const provider = getStorageProvider();
  return provider.getDismissedItemsWithExpiry();
}

export async function undismissItem(key: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.undismissItem(key);
}

// UI State
export async function setUIState(key: string, value: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.setUIState(key, value);
}

export function getUIState(key: string): string | null {
  const provider = getStorageProvider();
  return provider.getUIState(key);
}

export function getAllUIState(): Record<string, string> {
  const provider = getStorageProvider();
  return provider.getAllUIState();
}

export async function deleteUIState(key: string): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.deleteUIState(key);
}

export async function clearAllUIState(): Promise<{ success: boolean; error?: string }> {
  const provider = getStorageProvider();
  return provider.clearAllUIState();
}

// Cleanup
export async function clearExpiredActions(): Promise<{ success: boolean; deleted: number }> {
  const provider = getStorageProvider();
  return provider.clearExpiredActions();
}
